import React from 'react';
import { Form, Input, Radio, AutoComplete } from 'antd';
import FileUpload from 'components/form/FileUploadPrivate';
import W9UploadDesc from 'components/W9UploadDesc';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import { ENPIType } from 'types/credentialing';
import ExternalLinkIcon from 'components/MenuIcons/ExternalLinkIcon';
import SectionHeader from '../../components/SectionHeader';
import InfoTip from '../../components/InfoTip';
import SsnInput from '../../components/SsnInput';
import CardRadioGroup from '../../components/CardRadioGroup';
import CaqhCheck from '../../components/CaqhCheck';
import HiddenFormField from 'pages/InsuranceBillingOnboarding/components/HiddenFormField';
import dataSpringLogo from 'assets/credentialing/DataSpring.png';
import { ENTITY_TYPES, DISCLOSURES, TAX_ID_ERROR } from '../../constants';
import s from '../../s.module.less';

// disclosures 直接对应 DTO 数组：每项 { questionKey, answer(boolean), explanation }
const DISCLOSURE_INIT = DISCLOSURES.map((d) => ({ questionKey: d.id }));

export default function StepFinal() {
    const form = Form.useFormInstance();
    const [credentialInfo] = useCredentialInfo();
    const isGroupPractice = credentialInfo.billingNpiType?.value === ENPIType.PRACTICE_NPI;
    return (
        <>
            <div className={s.wizSection}>
                <SectionHeader
                    title={
                        <span>
                            CAQH
                            <InfoTip title="CAQH (Council for Affordable Quality Healthcare) is a nonprofit that maintains a centralized database of provider credentialing information. Payers use it to verify your licenses, education, and work history. Creating a CAQH profile is a standard, one-time step in getting credentialed." />
                        </span>
                    }
                />
                <Form.Item label="Do you have a CAQH account?" required>
                    {/* 10/23：CAQH 改名 DataSpring, powered by CAQH，provider 分不清两个名字——同一个账号、同一个登录 */}
                    <div className={s.caqhBrand}>
                        <span>CAQH is now</span>
                        <img src={dataSpringLogo} alt="DataSpring, powered by CAQH" />
                        <span>
                            It is the same account, and you still sign in at{' '}
                            <a href="https://proview.caqh.org" target="_blank" rel="noopener noreferrer">
                                proview.caqh.org
                            </a>
                            .
                        </span>
                    </div>
                    <Form.Item name="hasCaqhAccount" noStyle rules={[{ required: true, message: 'Required' }]}>
                        <CardRadioGroup
                            name="hasCaqhAccount"
                            options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false },
                            ]}
                        />
                    </Form.Item>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(p, c) => p.hasCaqhAccount !== c.hasCaqhAccount}>
                    {({ getFieldValue }) => {
                        const val = getFieldValue('hasCaqhAccount');
                        if (val === true) {
                            return (
                                <div className={s.compactGroup}>
                                    <Form.Item
                                        name="caqhUsername"
                                        label="CAQH username"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input size="large" placeholder="Your CAQH username" />
                                    </Form.Item>
                                    <Form.Item
                                        name="caqhPassword"
                                        label="CAQH password"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input.Password size="large" placeholder="Your CAQH password" />
                                    </Form.Item>
                                    {/* 只读的检查结果：注册成表单字段，CaqhCheck 才能 useWatch 到回填值 */}
                                    <Form.Item name="caqhCheck" noStyle>
                                        <HiddenFormField />
                                    </Form.Item>
                                    <CaqhCheck />
                                    <p className={s.hint}>
                                        We use your login to pull, verify, and keep your CAQH data up to date on your
                                        behalf. It&apos;s encrypted and never used for anything else. You confirm this
                                        when you sign at Review and confirm.
                                    </p>
                                </div>
                            );
                        }
                        if (val === false) {
                            return (
                                <div className={s.caqhAlert}>
                                    <svg
                                        className={s.caqhAlertIcon}
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <div className={s.caqhAlertBody}>
                                        <p className={s.caqhAlertText}>
                                            You&apos;ll need to create your own CAQH profile so payers can verify your
                                            credentials. It&apos;s free and takes about 15 minutes. You can still submit
                                            now and set it up after.
                                        </p>
                                        <a
                                            className={s.caqhAlertLink}
                                            href="https://proview.caqh.org/PR/Registration"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Register with CAQH
                                            <ExternalLinkIcon />
                                        </a>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    }}
                </Form.Item>
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="Social security number" />
                <div className={s.compactGroup}>
                    <Form.Item
                        name="ssn"
                        label="Social security number (SSN)"
                        rules={[
                            { required: true, message: 'Required' },
                            {
                                validator: (_, val) =>
                                    !val || String(val).replace(/\D/g, '').length === 9
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('SSN must be 9 digits')),
                            },
                        ]}
                    >
                        <SsnInput />
                    </Form.Item>
                    <p className={s.hint}>
                        Only the last 4 digits show. Use the eye to reveal it. Encrypted and used only for credentialing
                        and billing.
                    </p>
                </div>
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="Tax information" />
                <Form.Item name="legalTaxName" label="Legal tax name" rules={[{ required: true, message: 'Required' }]}>
                    <Input size="large" placeholder="Name as it appears on your W-9" />
                </Form.Item>
                <div className={s.wizRow}>
                    <Form.Item
                        name="taxId"
                        label="Tax ID / EIN"
                        rules={[
                            { required: true, message: 'Required' },
                            {
                                // Tax ID 可以填 EIN（XX-XXXXXXX）或个人 SSN（XXX-XX-XXXX）：只校验数字位数
                                validator: (_, val) =>
                                    !val || String(val).replace(/\D/g, '').length === 9
                                        ? Promise.resolve()
                                        : Promise.reject(new Error(TAX_ID_ERROR)),
                            },
                        ]}
                    >
                        <Input size="large" placeholder="XX-XXXXXXX or XXX-XX-XXXX" />
                    </Form.Item>
                    <Form.Item
                        name="businessEntityType"
                        label="Business entity type"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <AutoComplete
                            size="large"
                            placeholder="Select or enter"
                            options={ENTITY_TYPES.filter((t) => t !== 'Other').map((t) => ({ value: t }))}
                            filterOption={(input, option) =>
                                (option?.value as string).toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </div>
            </div>

            <div className={s.wizSection}>
                <div className={s.compactGroup}>
                    <SectionHeader title="W-9 form" />
                    <W9UploadDesc strong>
                        {isGroupPractice
                            ? "Please upload your practice's completed and signed W-9 form, used for the group's Tax ID / EIN."
                            : 'Please upload your completed and signed W-9 form.'}
                    </W9UploadDesc>
                    {/* 只在 Review 提交时由 setFields 标红（未触碰不校验），补传后 onChange 自动清除 */}
                    <Form.Item name="w9FormUrl" rules={[{ required: true, message: 'Upload your signed W-9 form' }]}>
                        <FileUpload uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" />
                    </Form.Item>
                </div>
            </div>

            <div className={s.wizSection}>
                <div className={s.compactGroup}>
                    <SectionHeader title="Payer participation" />
                    <Form.Item
                        name="hasFacilityPrivileges"
                        label="Do you have admitting privileges at a facility (e.g. an ambulatory surgery center)?"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Radio.Group
                            onChange={(e) => {
                                if (e.target.value !== true) form.setFieldValue('facilityName', null);
                            }}
                        >
                            <Radio value>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </Form.Item>
                </div>
                <Form.Item noStyle shouldUpdate={(p, c) => p.hasFacilityPrivileges !== c.hasFacilityPrivileges}>
                    {({ getFieldValue }) =>
                        getFieldValue('hasFacilityPrivileges') === true ? (
                            <Form.Item
                                name="facilityName"
                                label="Facility name"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input size="large" placeholder="Name of the facility" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="Disclosures" />
                <Form.List name="disclosures" initialValue={DISCLOSURE_INIT}>
                    {(fields) =>
                        fields.map((field, idx) => (
                            <div key={field.key} className={s.discItem}>
                                <Form.Item name={[field.name, 'questionKey']} hidden>
                                    <Input />
                                </Form.Item>
                                <Form.Item
                                    name={[field.name, 'answer']}
                                    label={DISCLOSURES[idx]?.q}
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Radio.Group>
                                        <Radio value>Yes</Radio>
                                        <Radio value={false}>No</Radio>
                                    </Radio.Group>
                                </Form.Item>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(p, c) =>
                                        p.disclosures?.[field.name]?.answer !== c.disclosures?.[field.name]?.answer
                                    }
                                >
                                    {({ getFieldValue }) =>
                                        getFieldValue(['disclosures', field.name, 'answer']) === true ? (
                                            <Form.Item
                                                name={[field.name, 'explanation']}
                                                rules={[{ required: true, message: 'Please provide details' }]}
                                            >
                                                <Input.TextArea rows={3} placeholder="Please provide details..." />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>
                            </div>
                        ))
                    }
                </Form.List>
            </div>
        </>
    );
}
